import type {
  ConditionalFormatRule,
  ThresholdConfig,
  DataBarConfig,
  ColorScaleConfig,
} from '../types/reportBuilder';

export interface CellStyle {
  color?: string;
  backgroundColor?: string;
  /** For data bars: a gradient background showing proportional fill. */
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundRepeat?: string;
}

/**
 * Evaluates a conditional format rule for a given cell value.
 * Returns CSS-like style properties to apply to the cell.
 */
export function evaluateFormat(
  value: unknown,
  rule: ConditionalFormatRule,
  allValues: number[], // all numeric values in the column (for data_bar / color_scale)
): CellStyle {
  const num = Number(value);
  if (isNaN(num)) return {};

  switch (rule.type) {
    case 'threshold':
      return evaluateThreshold(num, rule.config as ThresholdConfig);
    case 'data_bar':
      return evaluateDataBar(num, rule.config as DataBarConfig, allValues);
    case 'color_scale':
      return evaluateColorScale(num, rule.config as ColorScaleConfig, allValues);
    default:
      return {};
  }
}

function evaluateThreshold(value: number, config: ThresholdConfig): CellStyle {
  // Evaluate conditions in order — first match wins
  for (const cond of config.conditions) {
    let match = false;
    switch (cond.operator) {
      case 'gt': match = value > cond.value; break;
      case 'gte': match = value >= cond.value; break;
      case 'lt': match = value < cond.value; break;
      case 'lte': match = value <= cond.value; break;
      case 'eq': match = value === cond.value; break;
    }
    if (match) {
      return { color: cond.color, backgroundColor: cond.bgColor };
    }
  }
  return config.defaultBgColor ? { backgroundColor: config.defaultBgColor } : {};
}

function evaluateDataBar(value: number, config: DataBarConfig, allValues: number[]): CellStyle {
  const max = config.maxValue ?? Math.max(...allValues, 1);
  const pct = Math.min(Math.max((value / max) * 100, 0), 100);
  return {
    backgroundImage: `linear-gradient(to right, ${config.color} ${pct}%, transparent ${pct}%)`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
  };
}

function evaluateColorScale(value: number, config: ColorScaleConfig, allValues: number[]): CellStyle {
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  if (max === min) return { backgroundColor: config.minColor };

  const ratio = (value - min) / (max - min); // 0..1
  const color = config.midColor
    ? interpolateThreeColor(config.minColor, config.midColor, config.maxColor, ratio)
    : interpolateTwoColor(config.minColor, config.maxColor, ratio);

  return { backgroundColor: color };
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
}

function interpolateTwoColor(c1: string, c2: string, ratio: number): string {
  const [r1, g1, b1] = parseHex(c1);
  const [r2, g2, b2] = parseHex(c2);
  return toHex(
    r1 + (r2 - r1) * ratio,
    g1 + (g2 - g1) * ratio,
    b1 + (b2 - b1) * ratio,
  );
}

function interpolateThreeColor(c1: string, c2: string, c3: string, ratio: number): string {
  if (ratio <= 0.5) {
    return interpolateTwoColor(c1, c2, ratio * 2);
  }
  return interpolateTwoColor(c2, c3, (ratio - 0.5) * 2);
}
